import fs from 'fs';
import os from 'os';
import path from 'path';

const SORTLIST_DIR = path.join(os.homedir(), '.sortlist');
const CONFIG_PATH = path.join(SORTLIST_DIR, 'config.json');

export interface CliConfig {
  endpoint: string;
  /** The company every request acts for: its id or its slug. */
  company?: string;
  /**
   * Hermes' OAuth token endpoint, used for unattended runs with an application
   * credential. A different host from the federation endpoint.
   */
  tokenEndpoint?: string;
}

const DEFAULT_CONFIG: CliConfig = {
  endpoint: 'https://federation.sortlist.com/graphql',
};

function ensureDir(): void {
  if (!fs.existsSync(SORTLIST_DIR)) {
    fs.mkdirSync(SORTLIST_DIR, { recursive: true });
  }
}

export function loadConfig(): CliConfig {
  try {
    const raw = fs.readFileSync(CONFIG_PATH, 'utf-8');
    return { ...DEFAULT_CONFIG, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_CONFIG };
  }
}

export function saveConfig(config: Partial<CliConfig>): void {
  ensureDir();
  const current = loadConfig();
  const merged = { ...current, ...config };
  fs.writeFileSync(CONFIG_PATH, JSON.stringify(merged, null, 2) + '\n');
}

export function resetConfig(): void {
  try {
    fs.unlinkSync(CONFIG_PATH);
  } catch {
    // already gone
  }
}

/**
 * Drops the cached identity (user + company/agency context) while preserving
 * non-identity settings like the endpoint. Used by `logout` so the next login
 * can't surface the previous account's user and company.
 */
/**
 * Forgets who you were, keeping where you point.
 *
 * `endpoint` and `tokenEndpoint` describe an environment, not an identity — an
 * earlier version kept only the first, so logging out silently discarded the
 * token endpoint and the next `sl use application` failed on a missing setting
 * nobody remembered unsetting.
 */
export function clearIdentity(): void {
  const { endpoint, tokenEndpoint } = loadConfig();
  ensureDir();
  const kept: Partial<CliConfig> = { endpoint };
  if (tokenEndpoint) kept.tokenEndpoint = tokenEndpoint;
  fs.writeFileSync(CONFIG_PATH, JSON.stringify(kept, null, 2) + '\n');
}

export function getEndpoint(override?: string): string {
  if (override) return override;
  if (process.env.SL_ENDPOINT) return process.env.SL_ENDPOINT;
  return loadConfig().endpoint;
}

/**
 * Context variables that are auto-injected into generated commands.
 * These are read from config and can be overridden via CLI flags.
 */
export interface UserContext {
  company?: string;
}

export function getUserContext(): UserContext {
  const config = loadConfig();
  return { company: config.company };
}
